import { useMemo, useState } from "react";
import { Navigate, useLocation, Link } from "react-router";
import { useAppSelector } from "../../state/store";
import { Grid } from "@mui/material";
import { Row } from "../../components/Row";
import { Card } from "../../components/Card/Card";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { shuffle } from "../../util/generalUtil";
import { GamePlayer } from "../../types/generalTypes";

interface CardType {
  book: number;
  chapter: number;
  verse: number;
  type: "R" | "V";
  isEmpty: boolean;
}
export function Concentration() {
  const location = useLocation();
  useDocumentTitle("Concentration - Bible Quiz Academy");
  const material = useAppSelector((state) => state.material.material);
  const settings = useAppSelector((state) => state.settings);
  const chapters = settings.selectedChapters;
  const gameSettings = location.state?.concentration || {};

  const [players, setPlayers] = useState<{ name: string; score: number }[]>(
    shuffle(
      (gameSettings.players || []).map((player: GamePlayer) => ({
        name: player.name,
        score: 0,
      }))
    )
  );
  const [turn, setTurn] = useState(0);
  const [indexFlipped, setIndexFlipped] = useState<number[]>([]);
  const [matchResult, setMatchResult] = useState(false);

  const getRandomized = () => {
    // Generate the deck
    const deckSize = gameSettings.settings?.verses || 10;

    if (material.length === 0) return [];
    const allVerses: CardType[] = [];
    material.forEach((book, index) => {
      book.chapters.forEach((chapter, chapterIndex) => {
        if (chapters && chapters[index] && chapters[index][chapterIndex]) {
          chapter.forEach((verse, verseIndex) => {
            const chapterFromSettings = chapters[index][chapterIndex];
            const chapterSettings =
              typeof chapterFromSettings === "boolean"
                ? Array(chapter.length).fill(chapterFromSettings)
                : chapterFromSettings;
            if (chapterSettings[verseIndex] && verse.verse !== "No Verse") {
              allVerses.push({
                book: index,
                chapter: chapterIndex,
                verse: verseIndex,
                type: "R",
                isEmpty: false,
              });
            }
          });
        }
      });
    });
    shuffle(allVerses);
    const result: CardType[] = [];
    // One card for reference, another for the verse
    for (let i = 0; i < deckSize && i < allVerses.length; i++) {
      result.push(allVerses[i]);
      result.push({ ...allVerses[i], type: "V" });
    }
    // Shuffle the cards
    return shuffle(result);
  };
  const [randomized, setRandomized] = useState<CardType[]>(getRandomized());

  const isFinished = useMemo(
    () =>
      !randomized.some(
        (card, index) => !card.isEmpty && !indexFlipped.includes(index)
      ),
    [randomized, indexFlipped]
  );

  if (players.length === 0 || chapters.length === 0) return <Navigate to="/" />;

  return (
    <div className="page concentration-page">
      <Row>
        <Link className="secondary-btn" to="/">
          Go Back
        </Link>
        <h1 style={{ marginLeft: 10 }}>Concentration</h1>
      </Row>
      <div style={{ marginTop: 10, marginBottom: 20 }}>
        <div style={{ display: "flex" }}>
          <div style={{ width: 200 }}>Name</div>
          <div style={{ marginLeft: 10 }}>Score</div>
        </div>
        {players.map((player, index) => (
          <div key={player.name} style={{ display: "flex", marginTop: 4 }}>
            <div
              style={{
                width: 200,
                backgroundColor:
                  turn === index && !isFinished
                    ? settings.lightMode
                      ? "lightblue"
                      : "darkblue"
                    : undefined,
              }}
            >
              {player.name}
            </div>
            <div style={{ marginLeft: 10 }}>{player.score}</div>
          </div>
        ))}
      </div>
      {(!isFinished || indexFlipped.length > 0) && (
        <p>
          {indexFlipped.length === 2
            ? matchResult
              ? `You found a match! Click one of the cards to claim them and ${
                  !isFinished ? "take another turn" : "finsh the game"
                }.`
              : "Not a match. Click one of the cards to move to the next player."
            : "Turn over two cards to see if the reference and verse match!"}
        </p>
      )}
      {isFinished && indexFlipped.length === 0 && <p>Game over!</p>}
      <Grid container spacing={1}>
        {randomized.map((card, index) => {
          const flipped = indexFlipped.includes(index);
          const backText =
            card.type === "R"
              ? `${material[card.book].bookName} ${card.chapter + 1}:${
                  card.verse + 1
                }`
              : material[card.book].chapters[card.chapter][card.verse].verse;
          return (
            <Grid key={index}>
              <Card
                size={{
                  width: 280,
                  height: 120,
                }}
                onClick={() => {
                  if (indexFlipped.length === 2) {
                    // Flip back over
                    if (!flipped) return;
                    setIndexFlipped([]);
                    if (matchResult) {
                      // Remove the cards
                      const newCards = [...randomized];
                      indexFlipped.forEach((index) => {
                        newCards[index] = { ...newCards[index], isEmpty: true };
                      });
                      setRandomized(newCards);
                      // Increase score
                      const newPlayers = [...players];
                      newPlayers[turn] = {
                        ...newPlayers[turn],
                        score: newPlayers[turn].score + 2,
                      };
                      setPlayers(newPlayers);
                    } else {
                      // Next turn
                      setTurn((turn + 1) % players.length);
                    }
                    return;
                  }
                  if (indexFlipped.length === 1) {
                    if (flipped) return;
                    // Second card flipped; check for matching
                    const original = randomized[indexFlipped[0]];
                    const match =
                      original.book === card.book &&
                      original.chapter === card.chapter &&
                      original.verse === card.verse;
                    setMatchResult(match);
                  }

                  // Apply the flipped cards (either 1 or 2)
                  setIndexFlipped([...indexFlipped, index]);
                }}
                ariaLabel={flipped ? backText : "Flip This Card"}
                className={`${
                  flipped && indexFlipped.length === 2
                    ? ` ${matchResult ? "correct" : "incorrect"}`
                    : ""
                }`}
                style={{
                  visibility: card.isEmpty ? "hidden" : "visible",
                }}
                isFlipped={flipped || card.isEmpty}
                frontContent={`Card ${index + 1}`}
                backContent={backText}
                backStyle={{ fontSize: card.type === "V" ? 14 : 18 }}
              />
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
}
